import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

const categories = [
  { id: 'all', label: 'All Technologies', icon: 'ri-apps-line' },
  { id: 'frontend', label: 'Frontend', icon: 'ri-layout-masonry-line' },
  { id: 'backend', label: 'Backend', icon: 'ri-server-line' },
  { id: 'mobile', label: 'Mobile', icon: 'ri-smartphone-line' },
  { id: 'cloud', label: 'Cloud & DevOps', icon: 'ri-cloud-line' },
  { id: 'database', label: 'Databases', icon: 'ri-database-2-line' },
  { id: 'ai', label: 'AI & Data', icon: 'ri-brain-line' },
  { id: 'erp', label: 'ERP & Enterprise', icon: 'ri-building-2-line' },
];

interface TechDetail {
  name: string;
  category: string;
  icon: string;
  talentCount: number;
  avgExperience: string;
  avgRate: string;
  description: string;
  skills: string[];
  color: string;
  landingPageUrl?: string;
}

const technologies: TechDetail[] = [
  {
    name: 'React',
    category: 'frontend',
    icon: 'ri-reactjs-line',
    talentCount: 120,
    avgExperience: '4.2 yrs',
    avgRate: '$55–$85/hr',
    description: 'Component-based UI library for building fast, interactive web applications. Our React developers specialize in Next.js, TypeScript, and state management.',
    skills: ['Next.js', 'TypeScript', 'Redux', 'Tailwind'],
    color: 'bg-sky-50 text-sky-700 border-sky-200',
    landingPageUrl: '/hire-react-developers',
  },
  {
    name: 'Angular',
    category: 'frontend',
    icon: 'ri-angularjs-line',
    talentCount: 85,
    avgExperience: '5.1 yrs',
    avgRate: '$60–$90/hr',
    description: 'Enterprise-grade framework for large-scale applications. Our Angular engineers bring deep RxJS and NgRx expertise for complex stateful apps.',
    skills: ['RxJS', 'NgRx', 'TypeScript', 'Jasmine'],
    color: 'bg-red-50 text-red-700 border-red-200',
  },
  {
    name: 'Vue.js',
    category: 'frontend',
    icon: 'ri-vuejs-line',
    talentCount: 64,
    avgExperience: '3.8 yrs',
    avgRate: '$50–$75/hr',
    description: 'Progressive framework known for gentle learning curve and flexibility. Perfect for rapid prototyping and incrementally adoptable projects.',
    skills: ['Nuxt.js', 'Pinia', 'Vite', 'Composition API'],
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  {
    name: 'Node.js',
    category: 'backend',
    icon: 'ri-nodejs-line',
    talentCount: 140,
    avgExperience: '4.5 yrs',
    avgRate: '$55–$85/hr',
    description: 'Server-side JavaScript runtime powering scalable APIs and microservices. Our Node engineers excel at event-driven architecture.',
    skills: ['Express', 'NestJS', 'GraphQL', 'Socket.io'],
    color: 'bg-green-50 text-green-700 border-green-200',
    landingPageUrl: '/hire-nodejs-developers',
  },
  {
    name: 'Python',
    category: 'backend',
    icon: 'ri-code-s-slash-line',
    talentCount: 110,
    avgExperience: '4.8 yrs',
    avgRate: '$60–$95/hr',
    description: 'Versatile language for web backends, automation, and data pipelines. Our Python devs cover Django, FastAPI, and scientific computing.',
    skills: ['Django', 'FastAPI', 'Celery', 'Pandas'],
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    landingPageUrl: '/hire-python-developers',
  },
  {
    name: '.NET',
    category: 'backend',
    icon: 'ri-code-box-line',
    talentCount: 92,
    avgExperience: '6.2 yrs',
    avgRate: '$65–$95/hr',
    description: 'Microsoft enterprise ecosystem for robust, high-performance applications. Deep expertise in .NET Core, ASP.NET, and Azure integration.',
    skills: ['.NET Core', 'ASP.NET', 'Entity Framework', 'Azure'],
    color: 'bg-violet-50 text-violet-700 border-violet-200',
  },
  {
    name: 'Java',
    category: 'backend',
    icon: 'ri-cup-line',
    talentCount: 105,
    avgExperience: '6.5 yrs',
    avgRate: '$65–$100/hr',
    description: 'Battle-tested for enterprise systems, banking, and large-scale distributed architectures. Spring Boot specialists available.',
    skills: ['Spring Boot', 'Hibernate', 'Maven', 'Kafka'],
    color: 'bg-orange-50 text-orange-700 border-orange-200',
  },
  {
    name: 'React Native',
    category: 'mobile',
    icon: 'ri-reactjs-line',
    talentCount: 78,
    avgExperience: '3.9 yrs',
    avgRate: '$55–$80/hr',
    description: 'Cross-platform mobile development with a single codebase. Ideal for MVPs and apps needing fast iteration on both iOS and Android.',
    skills: ['Expo', 'Redux', 'Native Modules', 'TypeScript'],
    color: 'bg-sky-50 text-sky-700 border-sky-200',
  },
  {
    name: 'Flutter',
    category: 'mobile',
    icon: 'ri-flutter-line',
    talentCount: 56,
    avgExperience: '3.2 yrs',
    avgRate: '$50–$75/hr',
    description: 'Google UI toolkit for natively compiled apps. Known for beautiful, high-performance interfaces with customizable widgets.',
    skills: ['Dart', 'Firebase', 'BLoC', 'Riverpod'],
    color: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  },
  {
    name: 'iOS / Swift',
    category: 'mobile',
    icon: 'ri-apple-line',
    talentCount: 48,
    avgExperience: '5.3 yrs',
    avgRate: '$70–$110/hr',
    description: 'Native iOS development for premium user experiences. Our Swift engineers build App Store-ready apps with deep Apple ecosystem knowledge.',
    skills: ['SwiftUI', 'UIKit', 'CoreData', 'Combine'],
    color: 'bg-gray-50 text-gray-700 border-gray-200',
  },
  {
    name: 'AWS',
    category: 'cloud',
    icon: 'ri-amazon-line',
    talentCount: 130,
    avgExperience: '4.8 yrs',
    avgRate: '$65–$95/hr',
    description: 'Full-stack cloud infrastructure design, serverless architectures, and cost-optimized deployments on the world\'s largest cloud platform.',
    skills: ['Lambda', 'ECS', 'CloudFormation', 'RDS'],
    color: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  {
    name: 'Kubernetes',
    category: 'cloud',
    icon: 'ri-ship-line',
    talentCount: 72,
    avgExperience: '4.1 yrs',
    avgRate: '$70–$105/hr',
    description: 'Container orchestration at scale. Our K8s engineers design resilient, auto-scaling clusters with GitOps-based deployments.',
    skills: ['Helm', 'Istio', 'ArgoCD', 'Prometheus'],
    color: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  {
    name: 'Docker',
    category: 'cloud',
    icon: 'ri-box-3-line',
    talentCount: 95,
    avgExperience: '4.3 yrs',
    avgRate: '$55–$80/hr',
    description: 'Containerization expertise for consistent dev-to-prod pipelines. Multi-stage builds, compose stacks, and registry management.',
    skills: ['Compose', 'Swarm', 'Registry', 'CI/CD'],
    color: 'bg-sky-50 text-sky-700 border-sky-200',
  },
  {
    name: 'PostgreSQL',
    category: 'database',
    icon: 'ri-archive-line',
    talentCount: 88,
    avgExperience: '5.0 yrs',
    avgRate: '$55–$80/hr',
    description: 'Advanced relational database with JSON support, full-text search, and geospatial capabilities. Performance tuning experts.',
    skills: ['PostGIS', 'Partitioning', 'Replication', 'PL/pgSQL'],
    color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  },
  {
    name: 'MongoDB',
    category: 'database',
    icon: 'ri-leaf-line',
    talentCount: 76,
    avgExperience: '4.0 yrs',
    avgRate: '$50–$75/hr',
    description: 'Document-oriented NoSQL for flexible schemas and horizontal scaling. Ideal for content management, real-time analytics, and catalogs.',
    skills: ['Mongoose', 'Aggregation', 'Atlas', 'Sharding'],
    color: 'bg-green-50 text-green-700 border-green-200',
  },
  {
    name: 'Machine Learning',
    category: 'ai',
    icon: 'ri-brain-line',
    talentCount: 42,
    avgExperience: '3.5 yrs',
    avgRate: '$80–$130/hr',
    description: 'End-to-end ML pipelines: data preprocessing, model training, deployment, and monitoring. NLP, computer vision, and recommender systems.',
    skills: ['TensorFlow', 'PyTorch', 'Scikit-learn', 'MLOps'],
    color: 'bg-rose-50 text-rose-700 border-rose-200',
  },
  {
    name: 'Data Engineering',
    category: 'ai',
    icon: 'ri-bar-chart-box-line',
    talentCount: 58,
    avgExperience: '4.6 yrs',
    avgRate: '$65–$100/hr',
    description: 'ETL/ELT pipelines, data lakes, warehouse architecture, and real-time streaming. Snowflake, Databricks, and Apache Spark experts.',
    skills: ['Spark', 'Airflow', 'dbt', 'Kafka'],
    color: 'bg-teal-50 text-teal-700 border-teal-200',
  },
  {
    name: 'SAP',
    category: 'erp',
    icon: 'ri-building-4-line',
    talentCount: 35,
    avgExperience: '7.2 yrs',
    avgRate: '$90–$140/hr',
    description: 'Enterprise resource planning implementation, customization, and integration. SAP S/4HANA, Fiori, and ABAP development.',
    skills: ['S/4HANA', 'ABAP', 'Fiori', 'BTP'],
    color: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  {
    name: 'Oracle HCM',
    category: 'erp',
    icon: 'ri-user-settings-line',
    talentCount: 28,
    avgExperience: '6.8 yrs',
    avgRate: '$85–$130/hr',
    description: 'Human capital management cloud implementation, payroll integration, and workforce analytics for global enterprises.',
    skills: ['Core HR', 'Payroll', 'Talent', 'Analytics'],
    color: 'bg-red-50 text-red-700 border-red-200',
  },
  {
    name: 'Workday',
    category: 'erp',
    icon: 'ri-briefcase-4-line',
    talentCount: 22,
    avgExperience: '5.5 yrs',
    avgRate: '$80–$125/hr',
    description: 'Workday HCM and Financials configuration, integration, and reporting. Certified consultants for rapid deployment.',
    skills: ['HCM', 'Financials', 'Reporting', 'Integration'],
    color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  },
];

export default function TechStackBuilder() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05,
  });

  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedTechs, setSelectedTechs] = useState<string[]>([]);
  const [expandedTech, setExpandedTech] = useState<string | null>(null);

  const filteredTechs =
    activeCategory === 'all'
      ? technologies
      : technologies.filter((t) => t.category === activeCategory);

  const toggleTech = (name: string) => {
    setSelectedTechs((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
    setExpandedTech(name);
  };

  const selectedDetails = technologies.filter((t) =>
    selectedTechs.includes(t.name)
  );

  const totalTalent = selectedDetails.reduce((sum, t) => sum + t.talentCount, 0);

  const scrollToCTA = () => {
    const el = document.getElementById('cta');
    if (el) {
      const offset = 80;
      const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: pos, behavior: 'smooth' });
    }
  };

  return (
    <section
      id="technologies"
      className="py-16 md:py-24 bg-white relative overflow-hidden"
      ref={ref}
    >
      {/* Decorative */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-10 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-stack-line text-primary-600"></i>
            <span className="text-sm font-medium text-gray-700">
              Interactive Tech Explorer
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Build Your
            <br />
            <span className="text-primary-600">Dream Tech Stack</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Click technologies to explore talent availability, rates, and
            expertise. Assemble your stack and see how many vetted engineers we
            have ready to deploy.
          </motion.p>

          {/* Stat Badge Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-8 md:mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-4xl mx-auto"
          >
            {[
              { icon: 'ri-stack-line', value: '20+', label: 'Technologies', accent: 'bg-primary-50 border-primary-100 text-primary-700' },
              { icon: 'ri-user-star-line', value: '1,000+', label: 'Vetted Engineers', accent: 'bg-emerald-50 border-emerald-100 text-emerald-700' },
              { icon: 'ri-folders-line', value: '8', label: 'Categories', accent: 'bg-amber-50 border-amber-100 text-amber-700' },
              { icon: 'ri-time-line', value: '48h', label: 'Avg. Match Time', accent: 'bg-sky-50 border-sky-100 text-sky-700' },
            ].map((stat, idx) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.35 + idx * 0.06, ...springTransition }}
                className={`flex items-center gap-2.5 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-xl border ${stat.accent}`}
              >
                <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-white/70 flex-shrink-0">
                  <i className={`${stat.icon} text-base md:text-lg`}></i>
                </div>
                <div className="text-left">
                  <div className="text-sm md:text-base font-bold leading-tight">{stat.value}</div>
                  <div className="text-[10px] md:text-xs font-medium opacity-80">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Category Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="flex flex-wrap justify-center gap-2 md:gap-3 mb-8 md:mb-12"
        >
          {categories.map((cat) => {
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => {
                  setActiveCategory(cat.id);
                  setExpandedTech(null);
                }}
                className={`flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-full text-sm md:text-base font-medium transition-all duration-300 cursor-pointer whitespace-nowrap border ${
                  isActive
                    ? 'bg-primary-500 text-white border-primary-500 shadow-lg shadow-primary-500/20'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.label}
              </button>
            );
          })}
        </motion.div>

        {/* Main layout: Tech Grid + Selected Stack sidebar */}
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 relative">
          {/* Decorative corner accents */}
          <div className="absolute -top-4 -left-4 w-24 h-24 bg-primary-100/40 rounded-2xl -z-10 hidden lg:block"></div>
          <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-amber-100/40 rounded-2xl -z-10 hidden lg:block"></div>

          {/* Tech Cards Grid */}
          <div className="w-full lg:w-2/3">
            <motion.div
              layout
              className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5"
            >
              <AnimatePresence mode="popLayout">
                {filteredTechs.map((tech, index) => {
                  const isSelected = selectedTechs.includes(tech.name);
                  const isExpanded = expandedTech === tech.name;

                  return (
                    <motion.div
                      key={tech.name}
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{
                        ...springTransition,
                        duration: 0.4,
                        delay: index * 0.03,
                      }}
                      onClick={() => toggleTech(tech.name)}
                      className={`relative bg-white rounded-2xl border transition-all duration-300 cursor-pointer overflow-hidden ${
                        isSelected
                          ? 'border-primary-400 shadow-xl shadow-primary-500/10'
                          : 'border-gray-100 hover:border-primary-200 hover:shadow-lg'
                      }`}
                    >
                      {/* Selection indicator */}
                      {isSelected && (
                        <div className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center bg-primary-500 rounded-full">
                          <i className="ri-check-line text-white text-sm"></i>
                        </div>
                      )}

                      <div className="p-5 md:p-6">
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-3">
                          <div
                            className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl ${tech.color}`}
                          >
                            <i
                              className={`${tech.icon} text-lg md:text-xl`}
                            ></i>
                          </div>
                          <div>
                            <h3 className="text-base md:text-lg font-bold text-navy-900">
                              {tech.name}
                            </h3>
                          </div>
                        </div>

                        {/* Quick stats row */}
                        <div className="flex items-center gap-4 mb-3">
                          <div className="flex items-center gap-1.5">
                            <i className="ri-user-line text-gray-400 text-sm"></i>
                            <span className="text-sm font-semibold text-navy-900">
                              {tech.talentCount}
                            </span>
                            <span className="text-xs text-gray-500">
                              available
                            </span>
                          </div>
                          <div className="w-px h-4 bg-gray-200"></div>
                          <div className="flex items-center gap-1.5">
                            <i className="ri-time-line text-gray-400 text-sm"></i>
                            <span className="text-sm font-semibold text-navy-900">
                              {tech.avgExperience}
                            </span>
                            <span className="text-xs text-gray-500">
                              avg exp
                            </span>
                          </div>
                        </div>

                        {/* Skills pills */}
                        <div className="flex flex-wrap gap-1.5">
                          {tech.skills.map((skill) => (
                            <span
                              key={skill}
                              className="px-2 py-0.5 bg-gray-50 text-gray-600 text-xs font-medium rounded-md border border-gray-100"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Expanded detail panel */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="overflow-hidden"
                          >
                            <div className="px-5 md:px-6 pb-5 md:pb-6 pt-0">
                              <div className="border-t border-gray-100 pt-4">
                                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                                  {tech.description}
                                </p>
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`text-xs font-medium px-2.5 py-1 rounded-full ${tech.color}`}
                                  >
                                    {categories.find(
                                      (c) => c.id === tech.category
                                    )?.label || tech.category}
                                  </span>
                                  {tech.landingPageUrl && (
                                    <Link
                                      to={tech.landingPageUrl}
                                      className="text-xs font-medium text-primary-600 bg-primary-50 px-2.5 py-1 rounded-full border border-primary-100 hover:bg-primary-100 transition-colors cursor-pointer"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      Hire {tech.name} Developers
                                      <i className="ri-arrow-right-line ml-1 inline-block"></i>
                                    </Link>
                                  )}
                                  {isSelected ? (
                                    <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2.5 py-1 rounded-full border border-primary-100">
                                      In Your Stack
                                    </span>
                                  ) : (
                                    <span className="text-xs font-medium text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full border border-gray-100">
                                      Click to Add
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          </div>

          {/* Selected Stack Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ ...springTransition, duration: 0.8, delay: 0.4 }}
            className="w-full lg:w-1/3"
          >
            <div className="lg:sticky lg:top-28 relative">
              {/* Floating stat badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.6, ...springTransition }}
                className="absolute -top-3 -right-3 md:-top-4 md:-right-4 z-10 bg-white rounded-xl shadow-lg border border-primary-100 px-3 py-2 md:px-4 md:py-2.5 hidden lg:flex items-center gap-2"
              >
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                <span className="text-xs font-bold text-navy-900">Live Stack</span>
              </motion.div>

              <div className="bg-white rounded-2xl md:rounded-3xl border border-gray-100 shadow-xl p-6 md:p-8">
                <div className="flex items-center gap-3 mb-5 md:mb-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-500 rounded-xl">
                    <i className="ri-stack-line text-lg text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-navy-900">
                      Your Stack
                    </h3>
                    <p className="text-xs text-gray-500">
                      {selectedTechs.length} of {technologies.length} selected
                    </p>
                  </div>
                </div>

                {selectedTechs.length === 0 ? (
                  <div className="text-center py-8 md:py-10">
                    <div className="w-16 h-16 flex items-center justify-center bg-gray-50 rounded-full mx-auto mb-4">
                      <i className="ri-grid-line text-2xl text-gray-300"></i>
                    </div>
                    <p className="text-sm text-gray-500 mb-1">
                      No technologies selected yet
                    </p>
                    <p className="text-xs text-gray-400">
                      Click any tech card to add it to your stack
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3 mb-5 md:mb-6 max-h-[340px] overflow-y-auto pr-1 custom-scrollbar">
                      {selectedDetails.map((tech) => (
                        <motion.div
                          key={tech.name}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100"
                        >
                          <div
                            className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${tech.color}`}
                          >
                            <i className={`${tech.icon} text-sm`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-navy-900 truncate">
                              {tech.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {tech.talentCount} engineers
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTechs((prev) =>
                                prev.filter((n) => n !== tech.name)
                              );
                            }}
                            className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer flex-shrink-0"
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        </motion.div>
                      ))}
                    </div>

                    {/* Summary stats */}
                    <div className="grid grid-cols-2 gap-3 mb-5 md:mb-6">
                      <div className="bg-primary-50 rounded-xl p-3 text-center border border-primary-100">
                        <div className="text-xl md:text-2xl font-bold text-primary-600">
                          {totalTalent}
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5">
                          Total Engineers
                        </div>
                      </div>
                      <div className="bg-navy-50 rounded-xl p-3 text-center border border-navy-100">
                        <div className="text-xl md:text-2xl font-bold text-navy-700">
                          {selectedTechs.length}
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5">
                          Technologies
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={scrollToCTA}
                      className="w-full py-3 md:py-4 bg-primary-500 text-white text-sm md:text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
                    >
                      Request Talent for This Stack
                      <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300 inline-block"></i>
                    </button>

                    <p className="text-xs text-center text-gray-400 mt-3">
                      We will match you with pre-vetted engineers in 48 hours
                    </p>
                  </>
                )}
              </div>

              {/* Mini hint card */}
              <div className="mt-4 md:mt-5 bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0 mt-0.5">
                    <i className="ri-lightbulb-line text-amber-600 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-navy-900 mb-1">
                      Not sure what you need?
                    </h4>
                    <p className="text-xs text-gray-500 leading-relaxed mb-3">
                      Our technical recruiters can recommend the ideal stack for
                      your project goals and budget.
                    </p>
                    <button
                      onClick={scrollToCTA}
                      className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      Book a free consultation
                      <i className="ri-arrow-right-line ml-1 inline-block"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Find Talent by Skill Bridge */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, ...springTransition, duration: 0.6 }}
          className="mt-16 md:mt-24 pt-10 md:pt-14 border-t border-gray-100"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-navy-900 mb-1">
                Find Talent by Skill
              </h3>
              <p className="text-sm md:text-base text-gray-500">
                Jump directly to engineers with the expertise you need
              </p>
            </div>
            <Link
              to="/talent"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-navy-900 text-white text-sm font-semibold rounded-full hover:bg-navy-800 hover:shadow-lg hover:shadow-navy-900/20 transition-all duration-300 self-start sm:self-auto whitespace-nowrap"
            >
              Browse All Talent
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>
          </div>

          <div className="flex flex-wrap gap-2.5 md:gap-3">
            {[
              { name: 'React', icon: 'ri-reactjs-line', url: '/hire-react-developers' },
              { name: 'Python', icon: 'ri-code-s-slash-line', url: '/hire-python-developers' },
              { name: 'Node.js', icon: 'ri-nodejs-line', url: '/hire-nodejs-developers' },
              { name: 'Angular', icon: 'ri-angularjs-line' },
              { name: 'Vue.js', icon: 'ri-vuejs-line' },
              { name: 'React Native', icon: 'ri-smartphone-line' },
              { name: 'Flutter', icon: 'ri-flutter-line' },
              { name: 'AWS', icon: 'ri-amazon-line' },
              { name: 'Kubernetes', icon: 'ri-ship-line' },
              { name: 'PostgreSQL', icon: 'ri-database-2-line' },
              { name: 'Machine Learning', icon: 'ri-brain-line' },
              { name: 'Java', icon: 'ri-cup-line' },
              { name: '.NET', icon: 'ri-code-box-line' },
              { name: 'SAP', icon: 'ri-building-4-line' },
              { name: 'iOS / Swift', icon: 'ri-apple-line' },
            ].map((skill, idx) => (
              <motion.div
                key={skill.name}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.7 + idx * 0.03, ...springTransition }}
              >
                <Link
                  to={'url' in skill ? (skill as { url: string }).url : '/talent'}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:border-primary-300 hover:text-primary-700 hover:bg-primary-50 transition-all duration-300 whitespace-nowrap"
                >
                  <i className={`${skill.icon} text-primary-500`}></i>
                  {skill.name}
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>

      </div>
    </section>
  );
}